import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  MdNotifications,
  MdDoneAll,
  MdRefresh,
  MdChevronRight,
  MdDeleteOutline,
} from "react-icons/md";
import { toast } from "react-toastify";
import { NotificationsApi as NotificationApi } from "../../../api/common/apiIndex";
import Loader from "../../common/commonUtils/Loader";

const PANEL_WIDTH = 380;      
const PANEL_MIN_GUTTER = 8;  
const ARROW_MIN = 12;

const timeAgo = (iso) => {
  if (!iso) return "";
  const now = Date.now();
  const then = new Date(iso).getTime();
  const s = Math.max(1, Math.floor((now - then) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w}w`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo`;
  const y = Math.floor(d / 365);
  return `${y}y`;
};

export default function Notifications({ limit = 20, pollMs = 15000 }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null); // null until ready
  const anchorRef = useRef(null);
  const panelRef = useRef(null);
  const rafRef = useRef(0);

  const {
    data: latestPage,
    isLoading: latestLoading,
    refetch: refetchLatest,
  } = useQuery({
    queryKey: ["notifications", "latest", { page: 0, size: limit, sort: "createdAt,desc" }],
    queryFn: () => NotificationApi.latest({ page: 0, size: limit, sort: "createdAt,desc" }),
    refetchInterval: pollMs,
    staleTime: 5000,
  });

  const latest = latestPage?.content ?? [];

  const { data: unreadCountData, refetch: refetchCount } = useQuery({
    queryKey: ["notifications", "unreadCount"],
    queryFn: () => NotificationApi.unreadCount(),
    refetchInterval: pollMs,
    staleTime: 5000,
  });

  const unreadCount = unreadCountData?.count ?? 0;

  const markReadMutation = useMutation({
    mutationFn: (uuid) => NotificationApi.markRead(uuid),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === "notifications",
      });
    },
    onError: () => toast.error("Could not mark as read"),
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => NotificationApi.markAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === "notifications",
      });
    },
    onError: () => toast.error("Could not mark all as read"),
  });

  const deleteNotification = useMutation({
    mutationFn: (uuid) => NotificationApi.remove(uuid),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === "notifications",
      });
    },
    onError: (err) => {
      const msg = err?.response?.data?.message || err?.message || "Failed to delete notification.";
      toast.error(msg);
    },
  });

  const computeCoords = () => {
    const anchor = anchorRef.current;
    if (!anchor) return null;

    const r = anchor.getBoundingClientRect();
    const header = document.querySelector("header.header");
    const h = header?.getBoundingClientRect();

    const isNarrow = window.innerWidth < 576;
    const gutter = PANEL_MIN_GUTTER;

    const right = isNarrow ? gutter : Math.max(gutter, window.innerWidth - r.right);

    let width = Math.min(PANEL_WIDTH, window.innerWidth - right - gutter);

    const top = Math.min((h ? h.bottom : r.bottom) + gutter, window.innerHeight - gutter);

    const anchorCenterFromRight = window.innerWidth - (r.left + r.width / 2);
    let arrowRight = anchorCenterFromRight - right;
    arrowRight = Math.max(ARROW_MIN, Math.min(width - ARROW_MIN, arrowRight));

    return { top, right, width, arrowRight };
  };

  const openMenu = () => {
    const initial = computeCoords();
    setCoords(initial);
    setOpen(true);
  };
  const closeMenu = () => setOpen(false);

  useLayoutEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      const panel = panelRef.current;
      if (panel && (panel.contains(e.target) || anchorRef.current?.contains(e.target))) return;
      closeMenu();
    };
    const onKey = (e) => { if (e.key === "Escape") closeMenu(); };
    const onScroll = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => setCoords(computeCoords()));
    };
    const onResize = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => setCoords(computeCoords()));
    };

    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);

    setCoords(computeCoords());

    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
      cancelAnimationFrame(rafRef.current);
    };
  }, [open]);

  const handleItemClick = async (n) => {
    try {
      if (!n.read) await markReadMutation.mutateAsync(n.uuid);
      closeMenu();
      if (n.linkPath) navigate(n.linkPath);
    } catch {}
  };

  const hasItems = (latest?.length || 0) > 0;

  const bellBadge = useMemo(() => {
    if (!unreadCount) return null;
    const label = unreadCount > 99 ? "99+" : String(unreadCount);
    return (
      <span
        className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
        style={{ fontSize: "0.7rem" }}
        aria-label={`${label} unread notifications`}
      >
        {label}
      </span>
    );
  }, [unreadCount]);

  const refresh = async () => {
    await Promise.all([refetchLatest(), refetchCount()]);
  };

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className="btn btn-link position-relative p-0 ms-2"
        onClick={() => (open ? closeMenu() : openMenu())}
        aria-expanded={open}
        aria-label="Notifications"
        style={{ lineHeight: 0 }}
      >
        <MdNotifications size={33} color="#26b6f9ff" />
        {bellBadge}
      </button>

      {open &&
        createPortal(
          <div
            id="notif-menu"
            ref={panelRef}
            className="card shadow"
            style={{
              position: "fixed",
              top: coords?.top ?? -9999,          
              right: coords?.right ?? -9999,
              width: coords?.width ?? PANEL_WIDTH, 
              zIndex: 9999,
              visibility: coords ? "visible" : "hidden", 
              willChange: "top, right",
            }}
            role="menu"
          >
            <div
              style={{
                position: "absolute",
                top: -6,
                right: (coords?.arrowRight ?? ARROW_MIN),
                width: 12,
                height: 12,
                background: "var(--bs-body-bg, #fff)",
                transform: "rotate(45deg)",
                borderTop: "1px solid rgba(0,0,0,.1)",
                borderLeft: "1px solid rgba(0,0,0,.1)",
              }}
              aria-hidden="true"
            />
            <div className="card-header d-flex align-items-center justify-content-between">
              <div className="fw-semibold">Notifications</div>
              <div className="d-flex align-items-center gap-2">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={refresh}
                  title="Refresh"
                  aria-label="Refresh notifications"
                >
                  <MdRefresh size={16} />
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => markAllReadMutation.mutate()}
                  disabled={markAllReadMutation.isPending || unreadCount === 0}
                  title="Mark all as read"
                  aria-label="Mark all as read"
                >
                  <MdDoneAll size={16} /> <span className="ms-1 d-none d-sm-inline">Mark all</span>
                </button>
              </div>
            </div>

            <div className="card-body p-0" style={{ maxHeight: 520, overflowY: "auto" }}>
              {latestLoading ? (
                <div className="d-flex justify-content-center align-items-center py-5">
                  <Loader text="Loading..."/>
                </div>
              ) : !hasItems ? (
                <div className="px-3 py-4 text-center text-muted small">No notifications</div>
              ) : (
                <ul className="list-group list-group-flush">
                  {latest.map((n) => (
                    <li key={n.uuid} className="list-group-item p-2">
                      <button
                        className="w-100 text-start btn btn-link text-decoration-none p-0 d-flex align-items-start gap-2"
                        onClick={() => handleItemClick(n)}
                        style={{ whiteSpace: "normal" }}
                      >
                        <div className="pt-1">
                          {!n.read ? (
                            <span className="badge rounded-pill bg-primary-subtle text-primary-emphasis border border-primary-subtle">
                              New
                            </span>
                          ) : (
                            <span className="badge rounded-pill bg-secondary-subtle text-secondary-emphasis border">
                              Read
                            </span>
                          )}
                        </div>

                        <div className="flex-grow-1">
                          <div className="d-flex align-items-start justify-content-between gap-2">
                            <div className="fw-semibold">{n.title}</div>

                            <div className="d-flex align-items-center gap-2">
                              <div className="text-muted small">{timeAgo(n.createdAt)}</div>

                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0 text-danger"
                                title="Delete notification"
                                aria-label={`Delete notification ${n.title || ""}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  deleteNotification.mutate(n.uuid);
                                }}
                                disabled={deleteNotification.isPending}
                              >
                                <MdDeleteOutline size={18} />
                              </button>
                            </div>
                          </div>

                          <div className="text-muted small">{n.message}</div>

                          {n.linkPath ? (
                            <div className="text-primary small d-flex align-items-center">
                              Open <MdChevronRight className="ms-1" />
                            </div>
                          ) : null}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
